import type { BuilderConfig } from '~/lib/builder-types';

/**
 * Parse an exported JSON string and convert it back into a BuilderConfig.
 * Accepts either:
 * 1. A full BuilderConfig object (direct re-import)
 * 2. The export format from exportAsJson() (spec + preset + features array)
 *
 * Returns null if the JSON is invalid or cannot be converted.
 */
export function importFromJson(
  json: string,
  validFeatureSlugs: string[],
): BuilderConfig | null {
  try {
    const parsed = JSON.parse(json);

    // Case 1: Already a BuilderConfig (has .preset and .features as Record)
    if (parsed.preset && parsed.features && !Array.isArray(parsed.features)) {
      // Validate structure
      if (
        typeof parsed.preset !== 'string' ||
        typeof parsed.features !== 'object'
      ) {
        return null;
      }
      return parsed as BuilderConfig;
    }

    // Case 2: Export format { spec, preset, features: [...] }
    if (parsed.preset && Array.isArray(parsed.features)) {
      const validSlugs = new Set(validFeatureSlugs);
      const features: BuilderConfig['features'] = {};

      // Initialize all valid features as disabled
      for (const slug of validFeatureSlugs) {
        features[slug] = { enabled: false, options: {} };
      }

      // Enable features from the import
      for (const f of parsed.features) {
        if (typeof f.slug !== 'string') continue;
        if (!validSlugs.has(f.slug)) continue;
        features[f.slug] = {
          enabled: true,
          options: f.options ?? {},
        };
      }

      return {
        preset: parsed.preset,
        features,
      };
    }

    return null;
  } catch {
    return null;
  }
}
